/*
 * What chat puts on Home (VISUAL.md §7.8): a card for every conversation with something unread, and
 * the threads whose newest reply the reader has not seen. Core draws the greeting, the count line and
 * the empty states around these; what a conversation is, what its first unread line says and which
 * thread has moved are this module's to know, so the blocks arrive through the `homeSections` slot
 * (`CatchUpSection.tsx` is the entry the module registers).
 *
 * The order is core's selector (`unreadConversations`: the conversations that name the reader first,
 * then the server's newest-first order). A card is one link to the conversation, which opens at its
 * first unread by itself (`list/useLanding.ts`); what the card shows beside the name depends on what
 * the model holds. A loaded window that reaches past the read cursor yields the first unread line —
 * who wrote it, its first line, its time — and a cold conversation yields the count alone, because a
 * line fetched for a card would be a page fetched for a page the reader may never open — so a cold
 * card says since when instead, from the read cursor the bootstrap already carries: after a weekend
 * "#budget 5" is the sidebar's row again and "#budget 5 · since Friday 17:40" is a place to start.
 *
 * *Mark read* is the one control a card carries, drawn over the card's corner as the link's sibling
 * (a control inside a link is markup no browser resolves) and only on a surface whose dependencies
 * can move a cursor. It is the deliberate write (`auto: false`), the same one `Shift+Escape` makes.
 *
 * The threads block reads the loaded windows only: a root is listed while `threadUnread` answers
 * true for it, which is the same heuristic the timeline's footer dot uses (UX §4.1 R3), so Home and
 * the conversation never disagree about which thread is new.
 */

import { useMemo } from "react"
import { formatCapped, formatDayTime, formatTime } from "../../../core/i18n/index.ts"
import { useNow } from "../../../core/i18n/useNow.ts"
import { inPlaceClick } from "../../../core/shell/index.ts"
import { emptyModel, type ConversationId, type Model } from "../../../core/store/model.ts"
import { unreadConversations } from "../../../core/store/selectors.ts"
import { useModel } from "../../../core/store/store.ts"
import { cx } from "../../../lib/cx.ts"
import { Button, emojiText } from "../../../ui/index.ts"
import { conversationAddress } from "../conversation/title.ts"
import { chatDeps } from "../deps.tsx"
import { t } from "../i18n/t.ts"
import { conversationPath, dmPath } from "../navigation.ts"
import { chatSlice, lastReadSeqOf, threadUnread, withChatSlice } from "../state/selectors.ts"
import { THREAD_PARAM } from "../thread/openThread.ts"
import type { ConversationView, MessageView } from "../wire/views.ts"

/** The sidebar's rows cap at the wire's 999, and a card is wider than a row; the same cap. */
const COUNT_CAP = 999

/** How much of a message a card shows: one line, cut where a card's one line would be cut anyway. */
const LINE_MAX = 120

/** A conversation's address, or null for a direct one whose handle the bootstrap did not carry. */
function hrefOf(meta: ConversationView): string | null {
	if (meta.kind === "dm" || meta.kind === "groupDm") {
		return meta.dmHandle === undefined || meta.dmHandle === "" ? null : dmPath(meta.dmHandle)
	}
	return meta.slug === undefined || meta.slug === "" ? null : conversationPath(meta.slug)
}

/**
 * The first line of a message as a card quotes it: the first line that says something, with the
 * markdown's leading marks — a heading's hashes, a quote's chevron, a bullet — taken off, because a
 * card is not a renderer and a line that began "## " would read as a typo.
 */
export function firstLine(markdown: string): string {
	const line =
		markdown
			.split("\n")
			.map((raw) => raw.replace(/^[\s#>*\-`]+|^\d+\.\s+/, "").trim())
			.find((candidate) => candidate !== "") ?? ""
	return line.length > LINE_MAX ? `${line.slice(0, LINE_MAX).trimEnd()}…` : line
}

interface Card {
	conv: ConversationId
	href: string
	address: string
	unread: number
	mentions: number
	lastSeq: number
	/** The first unread message, when the loaded window holds it. */
	first: MessageView | undefined
	/**
	 * When this reader last read the conversation — the membership row's own stamp, which the bootstrap
	 * carries for every conversation. It is what a cold card says instead of a line it does not have.
	 */
	since: number | undefined
}

interface ThreadRow {
	conv: ConversationId
	href: string
	address: string
	root: MessageView
}

/**
 * A model view over the slices this section reads, for the selectors that take a whole model. The
 * slices are subscribed to one by one, so the section repaints when one of them moves and not when
 * anything else in the model does.
 */
function useModelView(): Model {
	const conversations = useModel((model) => model.conversations)
	const conversationOrder = useModel((model) => model.conversationOrder)
	const messages = useModel((model) => model.messages)
	const users = useModel((model) => model.users)
	const me = useModel((model) => model.me)
	const chat = useModel(chatSlice)
	return useMemo(
		() =>
			withChatSlice(
				{ ...emptyModel(), conversations, conversationOrder, messages, users, me },
				chat,
			),
		[conversations, conversationOrder, messages, users, me, chat],
	)
}

/**
 * What this screen reads out of one conversation's loaded window: the first line a card can quote,
 * and every root in it whose newest reply the reader has not seen.
 *
 * Both are one walk of the window, and the walk is remembered until something it is built from moves
 * — the window itself, where the reader stands in the conversation, their own replies, the name of
 * the place. Without that, a reader who leaves Home open paid a walk of **every loaded row of every
 * conversation** for every message that arrived anywhere: the section's model view is rebuilt by any
 * of six subscriptions, and at fifty conversations of a thousand rows that is fifty thousand rows
 * per message. Now only the conversation that moved is walked again.
 */
interface ConversationRead {
	firstUnread: MessageView | undefined
	threads: ThreadRow[]
}

/**
 * The answer and the rest of what it was read from. `users` stands in for the reader's own account as
 * well: the one event that changes `model.me` replaces the whole `users` map with it
 * (`core/store/applyEvent.ts`), and the account's id is all the address below takes from it.
 */
interface Memo extends ConversationRead {
	readSeq: number
	threadSeen: Record<number, number>
	meta: ConversationView
	users: Model["users"]
}

/**
 * What each window was read as, held *by the window* and weakly.
 *
 * The window is the memo's key rather than a field of it because the answer is worth exactly as much
 * as the rows it was read from, and those rows do not live for the life of the tab any more: a window
 * nobody has looked at for half an hour is given back (`state/trim.ts`), and an entry keyed by the
 * conversation would hold every row of it, its whole render tree and its first unread message
 * strongly, in a module nothing ever unmounts — so the memory this screen's memo saves in walks would
 * be paid back, several times over, in rows that cannot be collected. A weak key gives the answer the
 * lifetime of the question: the window goes, and the memo goes with it.
 */
let reads = new WeakMap<Model["messages"][number], Memo>()

/** A conversation with no window loaded: nothing to walk, and so nothing to remember either. */
const NOTHING_READ: ConversationRead = { firstUnread: undefined, threads: [] }

/** Forgets what every conversation was read as. For tests, which drive many models through one module. */
export function forgetCatchUpReads(): void {
	reads = new WeakMap()
}

function readOf(model: Model, conv: ConversationId): ConversationRead {
	const slice = model.conversations[conv]
	const window = model.messages[conv]
	const meta = slice?.meta as ConversationView | undefined
	if (window === undefined || meta === undefined) return NOTHING_READ
	const chat = chatSlice(model)
	const readSeq = lastReadSeqOf(model, conv)
	const held = reads.get(window)
	if (
		held !== undefined &&
		held.readSeq === readSeq &&
		held.threadSeen === chat.threadSeen &&
		held.meta === meta &&
		held.users === model.users
	) {
		return held
	}
	const threads: ThreadRow[] = []
	let firstUnread: MessageView | undefined
	const href = hrefOf(meta)
	const address = conversationAddress(meta, model.users, model.me?.id ?? 0)
	for (const row of window.rows as MessageView[]) {
		if (
			firstUnread === undefined &&
			row.seq > readSeq &&
			row.authorKind !== "system" &&
			row.deletedAt === undefined &&
			row.threadRootId === undefined
		) {
			firstUnread = row
		}
		if (href === null || (row.replyCount ?? 0) === 0 || row.deletedAt !== undefined) continue
		if (!threadUnread(model, conv, row)) continue
		threads.push({ conv, href, address, root: row })
	}
	const answer: Memo = {
		firstUnread,
		threads,
		readSeq,
		threadSeen: chat.threadSeen,
		meta,
		users: model.users,
	}
	reads.set(window, answer)
	return answer
}

function cardsOf(model: Model): Card[] {
	const meId = model.me?.id ?? 0
	return unreadConversations(model).flatMap((slice) => {
		const meta = slice.meta as ConversationView
		const href = hrefOf(meta)
		if (href === null) return []
		return [
			{
				conv: meta.id,
				href,
				address: conversationAddress(meta, model.users, meId),
				unread: slice.unread,
				mentions: slice.mentions,
				lastSeq: slice.lastSeq,
				first: readOf(model, meta.id).firstUnread,
				since: chatSlice(model).self[meta.id]?.lastReadAt,
			},
		]
	})
}

function threadsOf(model: Model): ThreadRow[] {
	const rows: ThreadRow[] = []
	for (const conv of model.conversationOrder) {
		if (model.conversations[conv] === undefined || model.messages[conv] === undefined) continue
		rows.push(...readOf(model, conv).threads)
	}
	return rows
}

function CatchUpCard({
	card,
	authorOf,
	now,
}: {
	card: Card
	authorOf: (id: number) => string
	/** The interface's one clock, so "since Yesterday 17:40" turns into a date at midnight like every other stamp. */
	now: number
}) {
	const deps = chatDeps()
	const named = card.mentions > 0
	return (
		<li className="tb-home__card">
			<a className="tb-home__card-link" href={card.href} onClick={inPlaceClick(card.href)}>
				<span className="tb-home__card-head">
					<span className="tb-home__card-name">{emojiText(card.address)}</span>
					<span
						className={cx("tb-pill", named ? "tb-pill--mention" : "tb-pill--count")}
						aria-label={t("chat.home.unreadCount", { count: card.unread })}
					>
						{formatCapped(card.unread, COUNT_CAP)}
					</span>
				</span>
				{card.first !== undefined ? (
					<>
						<span className="tb-home__card-line">
							<span className="tb-home__card-author">
								{emojiText(authorOf(card.first.authorId ?? 0))}
								{": "}
							</span>
							{firstLine(card.first.markdown)}
						</span>
						<span className="tb-home__card-meta">
							{named ? <span>{`${t("chat.home.mention")} · `}</span> : null}
							<time dateTime={new Date(card.first.createdAt).toISOString()}>
								{formatTime(card.first.createdAt)}
							</time>
						</span>
					</>
				) : card.since === undefined || card.since === 0 ? null : (
					/*
					 * A cold card has no line to quote — the window it would have come from was never loaded —
					 * and a card saying only "#budget 5" is the sidebar's row written larger (N5-R1-1). What it
					 * can always say is where to start: the moment this reader stopped reading, which the
					 * membership row carries for every conversation the bootstrap names.
					 */
					<span className="tb-home__card-meta">
						{named ? <span>{`${t("chat.home.mention")} · `}</span> : null}
						<time dateTime={new Date(card.since).toISOString()}>
							{t("chat.home.since", { when: formatDayTime(card.since, now) })}
						</time>
					</span>
				)}
			</a>
			{deps.markRead === undefined ? null : (
				<Button
					variant="ghost"
					quiet
					size="sm"
					className="tb-home__card-read"
					label={t("chat.home.markRead")}
					aria-label={t("chat.home.markReadOf", { conversation: card.address })}
					onClick={() =>
						void deps
							.markRead?.({ conv: card.conv, seq: card.lastSeq, auto: false })
							?.catch(() => undefined)
					}
				/>
			)}
		</li>
	)
}

export function CatchUp() {
	const model = useModelView()
	const cards = useMemo(() => cardsOf(model), [model])
	const threads = useMemo(() => threadsOf(model), [model])
	const authorOf = (id: number) => model.users[id]?.displayName ?? ""
	const now = useNow()
	if (cards.length === 0 && threads.length === 0) return null
	return (
		<>
			{cards.length === 0 ? null : (
				<section className="tb-home__section" aria-labelledby="tb-home-catchup">
					<h2 id="tb-home-catchup">{t("chat.home.catchUp")}</h2>
					<ul className="tb-home__cards">
						{cards.map((card) => (
							<CatchUpCard key={card.conv} card={card} authorOf={authorOf} now={now} />
						))}
					</ul>
				</section>
			)}
			{threads.length === 0 ? null : (
				<section className="tb-home__section" aria-labelledby="tb-home-threads">
					<h2 id="tb-home-threads">{t("chat.home.threads")}</h2>
					<ul className="tb-home__threads">
						{threads.map(({ conv, href, address, root }) => {
							const to = `${href}?${THREAD_PARAM}=${root.id}`
							return (
								<li key={`${conv}:${root.id}`}>
									<a className="tb-home__thread" href={to} onClick={inPlaceClick(to)}>
										<span className="tb-home__thread-line">
											<span className="tb-home__card-author">
												{emojiText(authorOf(root.authorId ?? 0))}
												{": "}
											</span>
											{firstLine(root.markdown)}
										</span>
										<span className="tb-tag">{t("chat.home.newReplies")}</span>
										<span className="tb-home__thread-place">{emojiText(address)}</span>
									</a>
								</li>
							)
						})}
					</ul>
				</section>
			)}
		</>
	)
}
